import React, { useRef, useState } from 'react';
import { DocumentIcon, XMarkIcon } from '@heroicons/react/24/outline';

import { extractPdfContent } from '../../utils/pdfjs';
import { Button } from '../Button';

import { Label } from './utils/Label';
import { SubLabel } from './utils/SubLabel';

interface FileUploadProps {
  id: string;
  label?: string;
  disabled?: boolean;
  onChange: (text: string | null, error?: Error) => void;
  subLabel?: string;
  showSubLabel?: boolean;
  errorMessage?: string;
  setError?: (id: string, error: { type: string; message: string }) => void;
  clearErrors?: (id: string) => void;
  className?: string;
  name: string;
  onBlur?: () => void;
}

// Helper functions
const validatePdfSignature = (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const fileReader = new FileReader();
    
    fileReader.onerror = () => {
      resolve(false);
    };
    
    fileReader.onloadend = (e) => {
      if (!e.target || !e.target.result) {
        resolve(false);
        return;
      }
      
      const arr = new Uint8Array(e.target.result as ArrayBuffer);
      
      // Check file is large enough to contain signature
      if (arr.length < 5) {
        resolve(false);
        return;
      }
      
      // PDF files start with %PDF- (25 50 44 46 2D in hex)
      const isPdfSignature = 
          arr[0] === 0x25 && // %
          arr[1] === 0x50 && // P
          arr[2] === 0x44 && // D
          arr[3] === 0x46 && // F
          arr[4] === 0x2D;   // -
          
      if (!isPdfSignature) {
        resolve(false);
        return;
      }
      
      // Additional check for version (optional)
      if (arr.length >= 8) {
        const hasValidVersion = 
            arr[5] === 0x31 && // 1
            arr[6] === 0x2E && // .
            (arr[7] >= 0x30 && arr[7] <= 0x39); // 0-9
        
        // We'll still accept the file if just the %PDF- signature is valid
        resolve(true);
      } else {
        resolve(true); // At minimum, we found %PDF-
      }
    };
    
    // Read only the first 8 bytes of the file to check the signature
    const blob = file.slice(0, 8);
    fileReader.readAsArrayBuffer(blob);
  });
};

const isValidPdfMimeType = (file: File): boolean => {
  const validMimeTypes = ['application/pdf'];
  return validMimeTypes.includes(file.type);
};

const isValidFileSize = (file: File, maxSizeMB: number): boolean => {
  const BYTES_PER_KILOBYTE = 1024;
  const KILOBYTES_PER_MEGABYTE = 1024;
  
  const fileSizeMB = file.size / (BYTES_PER_KILOBYTE * KILOBYTES_PER_MEGABYTE);
  return fileSizeMB <= maxSizeMB;
};

const getMaxFileSizeMB = (): number => {
  const maxSize = Number(process.env.NEXT_PUBLIC_MAX_PDF_SIZE) || 2;
  return isNaN(maxSize) ? 2 : maxSize;
};

export function FileUpload({
  id,
  label = '',
  disabled = false,
  onChange,
  subLabel = '',
  showSubLabel = false,
  errorMessage = '',
  setError = null,
  clearErrors = null,
  name,
  ...props
}: FileUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [internalError, setInternalError] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');

  // Helper to handle errors consistently
  const handleError = (message: string, error?: any) => {
    setFileName('');
    setInternalError(message);
    
    // Reset the file input
    if (inputRef.current) {
      inputRef.current.value = '';
    }

    if (setError && id) {
      setError(id, {
        type: 'manual',
        message,
      });
    }
    
    onChange(null, error instanceof Error ? error : new Error(message));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    // Clear any previous errors
    clearErrors && clearErrors(id);
    setInternalError('');
    setFileName('');
    
    const files = e.target.files;
    if (!files || files.length === 0) {
      return;
    }
    
    const selectedFile = files[0];
    
    // Basic MIME type validation (first layer)
    if (!isValidPdfMimeType(selectedFile)) {
      const message = `Invalid file type: ${selectedFile.type}. Please upload a valid PDF file.`;
      handleError(message);
      return;
    }

    try {
      // Advanced file signature validation (second layer)
      const isPdfValid = await validatePdfSignature(selectedFile);
      
      if (!isPdfValid) {
        const message = 'Invalid PDF file. The file appears to be corrupted or not a genuine PDF.';
        handleError(message);
        return;
      }
      
      // File size validation
      const maxFileSizeMB = getMaxFileSizeMB();
      if (!isValidFileSize(selectedFile, maxFileSizeMB)) {
        const message = `File size must be less than ${maxFileSizeMB} MB`;
        handleError(message);
        return;
      }
      
      // Only set the file name after all validations have passed
      setFileName(selectedFile.name);
      
      // Try to extract content - this provides an additional layer of validation
      const fileURL = URL.createObjectURL(selectedFile);
      extractPdfContent(fileURL)
        .then((extractedText) => {
          onChange(extractedText);
        })
        .catch((error) => {
          const message = 'Error processing PDF file. The file may be corrupted or password-protected.';
          handleError(message, error);
        });
    } catch (error) {
      const message = 'Error validating file: ' + (error instanceof Error ? error.message : String(error));
      handleError(message, error);
    }
  };

  // Handle file removal
  const handleRemoveFile = () => {
    // Reset the file input
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    setFileName('');
    setInternalError('');
    onChange(null);
    clearErrors && clearErrors(id);
  };

  // Check if there's an error from any source
  const hasError = !!(errorMessage || internalError);

  return (
    <div className={props.className}>
      {label && <Label id={`${id}-label`}>{label}</Label>}
      {showSubLabel && <SubLabel id={`${id}-sub-label`}>{subLabel}</SubLabel>}
      
      <div className="flex flex-col">
        <input
          ref={inputRef}
          accept=".pdf,application/pdf"
          disabled={disabled}
          id={id}
          name={name}
          type="file"
          onChange={handleFileChange}
          className={fileName && !hasError ? "hidden" : "block"}
        />
        
        {fileName && !hasError && (
          <div className="flex items-center mt-2 mb-1">
            <div className="flex items-center bg-gray-100 rounded p-2">
              <DocumentIcon className="w-5 h-5 text-gray-500 mr-2" />
              <span className="text-sm text-gray-700">{fileName}</span>
            </div>
            <Button
              type="button"
              className="ml-2 text-red-500 hover:text-red-700 focus:outline-none"
              onClick={handleRemoveFile}
            >
              <XMarkIcon className="w-5 h-5" />
            </Button>
          </div>
        )}
        
        {hasError && (
          <p className="text-red-500 mt-1">
            {errorMessage || internalError}
          </p>
        )}
      </div>
    </div>
  );
}
